import { Flex, Text } from "../cores";
import { usePagination, useViewContext } from "../hooks";
import { DOTS } from "../hooks/usePagination";
import { IconChevronLeft, IconChevronRight } from "../icons";

interface IPagination {
  onPageChange: (page: number | string) => void;
  totalCount: number;
  siblingCount?: number;
  currentPage: number | string;
  pageSize: number;
}

const Pagination = ({
  onPageChange,
  totalCount,
  siblingCount = 1,
  currentPage,
  pageSize,
}: IPagination) => {
  const { isMobile } = useViewContext();
  const paginationRange = usePagination({
    currentPage,
    totalCount,
    siblingCount,
    pageSize,
  });
  const totalPageCount = Math.ceil(totalCount / pageSize);
  if (currentPage === 0 || paginationRange.length < 2) {
    return null;
  }
  const onNext = () => {
    onPageChange((currentPage as number) + 1);
  };

  const onPrevious = () => {
    onPageChange((currentPage as number) - 1);
  };

  return (
    <Flex
      sx={{
        padding: 0,
        listStyleType: "none",
        backgroundColor: "quinaryScale.2",
        justifyContent: "center",
        alignItems: "center",
        paddingY: "5px",
        overflow: "hidden",
      }}
    >
      <Flex
        as={"ul"}
        sx={{
          padding: 0,
          position: "relative",
        }}
      >
        {currentPage <= 1 || isMobile ? null : (
          <Flex as={"li"}>
            <Flex
              onClick={onPrevious}
              as={"button"}
              sx={{
                border: "none",
                backgroundColor: "transparent",
                height: "32px",
                textAlign: "center",
                marginY: "auto",
                marginX: "2xs",
                boxSizing: "border-box",
                alignItems: "center",
                letterSpacing: "0.01071em",
                borderRadius: "16px",
                fontSize: "16px",
                minWidth: "fit-content",
                justifyContent: "center",
                position: "absolute",
                left: -45,

                color: "primaryScale.8",
                pointerEvents: currentPage <= 1 ? "none" : "auto",

                "@media (hover: hover)": {
                  "&:hover": {
                    cursor: currentPage <= 1 ? "default" : "pointer",
                    backgroundColor: "senaryScale.4",
                  },
                },
                "@media (hover: none)": {
                  "&:active": {
                    cursor: currentPage <= 1 ? "default" : "pointer",
                    backgroundColor: "senaryScale.4",
                  },
                },
              }}
            >
              <IconChevronLeft height={30} width={30} />
            </Flex>
          </Flex>
        )}
        {paginationRange?.map((pageNumber, index) => {
          if (pageNumber === DOTS) {
            return (
              <Flex
                key={index}
                as={"li"}
                sx={{
                  color: "primaryScale.8",
                  paddingX: "sm",
                  height: "32px",
                  textAlign: "center",
                  marginY: "auto",
                  marginX: "2xs",
                  boxSizing: "border-box",
                  alignItems: "center",
                  letterSpacing: "0.01071em",
                  borderRadius: "16px",
                  fontSize: "16px",
                  minWidth: "fit-content",
                  pointerEvents: "none",
                }}
              >
                &#8230;
              </Flex>
            );
          }

          return (
            <Flex as={"li"} key={index}>
              <Flex
                onClick={() => onPageChange(pageNumber)}
                as={"button"}
                sx={{
                  border: "none",
                  paddingX: "sm",
                  height: "32px",
                  textAlign: "center",
                  marginY: "auto",
                  marginX: "2xs",
                  boxSizing: "border-box",
                  alignItems: "center",
                  letterSpacing: "0.01071em",
                  borderRadius: "16px",
                  fontSize: "16px",
                  minWidth: "fit-content",
                  backgroundColor:
                    pageNumber === currentPage
                      ? "senaryScale.7"
                      : "transparent",
                  color:
                    pageNumber !== currentPage ? "primaryScale.8" : "white",
                  "@media (hover: hover)": {
                    "&:hover": {
                      cursor: pageNumber !== currentPage ? "pointer" : "auto",
                      backgroundColor:
                        pageNumber !== currentPage ? "senaryScale.4" : "none",
                    },
                  },
                  "@media (hover: none)": {
                    "&:active": {
                      cursor: pageNumber !== currentPage ? "pointer" : "auto",
                      backgroundColor:
                        pageNumber !== currentPage ? "senaryScale.4" : "none",
                    },
                  },
                }}
              >
                <Text>{pageNumber}</Text>
              </Flex>
            </Flex>
          );
        })}
        {currentPage >= totalPageCount || isMobile ? null : (
          <Flex as={"li"}>
            <Flex
              tabIndex={currentPage >= totalPageCount || isMobile ? null : 0}
              onClick={onNext}
              as={"button"}
              sx={{
                border: "none",
                backgroundColor: "transparent",
                pointerEvents: currentPage >= totalPageCount ? "none" : "auto",
                cursor: currentPage >= totalPageCount ? "default" : "pointer",
                height: "32px",
                textAlign: "center",
                marginY: "auto",
                marginX: "2xs",
                boxSizing: "border-box",
                alignItems: "center",
                letterSpacing: "0.01071em",
                borderRadius: "16px",
                fontSize: "16px",
                minWidth: "fit-content",
                justifyContent: "center",
                color: "primaryScale.8",
                position: "absolute",
                right: -45,

                "@media (hover: hover)": {
                  "&:hover": {
                    backgroundColor: "senaryScale.4",
                  },
                },
                "@media (hover: none)": {
                  "&:active": {
                    backgroundColor: "senaryScale.4",
                  },
                },
              }}
            >
              <IconChevronRight height={30} width={30} />
            </Flex>
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};

export default Pagination;
